import { ReactNode, useState } from "react";
import { Accessor, DataIntegritySource, Scalar } from "../types/types";
import ConfirmationModal from "../components/Modal/ConfirmationModal";
import Input from "../components/UI/Input/Input";
import { useTeamContext } from "../contexts/TeamContext";
import { useRestClientContext } from "../contexts/RestClientContext";
import Checkbox from "../components/UI/Checkbox/Checkbox";
import usePropertyAccessors, { TypesByAccessor } from "./usePropertyAccessors";
import { usePropertyEditContext } from "../contexts/PropertyEditContext";
import PropertyFieldSelect from "../components/UI/PropertyFieldSelect/PropertyFieldSelect";
import { useUserContext } from "../contexts/UserContext";
import DataIntegrityDropdown from "../components/UI/DataTable/DataIntegrityDropdown";

interface UseBulkEditModalProps {
	selectedIds: number[];
	typesByAccessor: TypesByAccessor;
	refresh: () => void;
}

export default function useBulkEditModal({ selectedIds, typesByAccessor, refresh }: UseBulkEditModalProps) {
	const client = useRestClientContext();
	const team = useTeamContext();
	const { getFriendlyColumnName } = usePropertyAccessors();
	const editing = usePropertyEditContext();
	const user = useUserContext();

	const [isOpen, setIsOpen] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [accessor, setAccessor] = useState<Accessor>();
	const [value, setValue] = useState<Scalar>();
	const [integrity, setIntegrity] = useState<DataIntegritySource | undefined>(undefined);

	const onClose = () => setIsOpen(false);
	const onConfirm = () => {
		setIsUpdating(true);

		const data: Partial<Record<string, Scalar>> = {
			[accessor as string]: value,
		};

		if (user.is_admin && integrity !== undefined) {
			data["data_integrity.integrity"] = JSON.stringify({ [accessor as string]: integrity });
		}

		// TODO: New v3 endpoint that takes ids and accessors / values separately
		client.v3
			.updateTeamProperties(team, selectedIds, data as Partial<Record<Accessor, Scalar>>)
			.then(() => {
				setIsOpen(false);
				refresh();
			})
			.finally(() => setIsUpdating(false));
	};

	let inner: ReactNode;

	if (isOpen) inner = <>{getFriendlyColumnName(accessor!)}</>;

	let input: ReactNode = (
		<Input placeholder="Enter new value..." disabled={isUpdating} onChange={(e, { value }) => setValue(value)} />
	);

	const columnMetadatum = typesByAccessor[accessor!];

	if (accessor && editing[accessor]) {
		input = (
			<PropertyFieldSelect
				field={accessor}
				onChange={(value: {
					target: {
						value: string;
					};
				}) => setValue(value.target.value)}
			/>
		);
	} else if (columnMetadatum) {
		switch (columnMetadatum.datatype) {
			case "integer":
			case "float":
			case "double":
				let step: number | string = 1;

				if (columnMetadatum.datatype === "float" || columnMetadatum.datatype === "double") step = "any";

				input = (
					<Input
						placeholder="Enter new value..."
						disabled={isUpdating}
						type="number"
						step={step}
						onChange={(e, { value }) => setValue(value)}
					/>
				);

				break;

			case "boolean":
				input = <Checkbox toggle onChange={(e, data) => setValue(data.checked)} />;

				break;

			case "datetime":
				input = (
					<Input disabled={isUpdating} type="datetime-local" onChange={(e, { value }) => setValue(value)} />
				);

				break;
		}
	}

	return {
		component: (
			<ConfirmationModal
				open={isOpen}
				title="Bulk edit"
				buttonsDisabled={isUpdating}
				onConfirm={onConfirm}
				onCancel={onClose}
				className={"bulk-edit-confirm-modal"}
			>
				<p>
					Editing {inner} for {selectedIds.length} properties
				</p>
				<div className="bulk-edit-input-row">
					{input}
					{user.is_admin && (
						<DataIntegrityDropdown value={integrity} onChange={setIntegrity} disabled={isUpdating} />
					)}
				</div>
			</ConfirmationModal>
		),
		open: (accessor: Accessor) => {
			setAccessor(accessor);
			setIntegrity(undefined);
			setIsOpen(true);
		},
	};
}
